import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search, ArrowRight, HelpCircle } from "lucide-react";
import Seo from "@/components/Seo";
import PageHero from "@/components/PageHero";
import ServiceCard from "@/components/ServiceCard";
import CTABand from "@/components/CTABand";
import { Input } from "@/components/ui/input";
import { SERVICE_CATEGORIES } from "@/lib/constants";
import api from "@/lib/api";

export default function Services() {
  const [all, setAll] = useState([]);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("all");

  useEffect(() => {
    api.get("/public/services").then((r) => setAll(r.data)).catch(() => {});
  }, []);

  const filtered = all.filter((s) => {
    const matchCat = cat === "all" || s.category === cat;
    const t = q.toLowerCase();
    const matchQ = !t || s.title.toLowerCase().includes(t) || (s.short_desc || "").toLowerCase().includes(t);
    return matchCat && matchQ;
  });

  return (
    <>
      <Seo title="Our Services | ApexPath UK Immigration Advice"
        description="Browse ApexPath's full range of UK immigration services. Search and filter by category to find the support that fits your circumstances." />
      <PageHero
        crumbs={[{ label: "Home", to: "/" }, { label: "Our Services" }]}
        eyebrow="Our Services"
        title="Our Immigration" highlight="Services"
        image="/images/consult.jpg"
        subtitle="Practical, personalised and up-to-date immigration advice for individuals, families and businesses. Search below to find the service that fits your circumstances.">
        <div className="relative max-w-xl">
          <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            data-testid="service-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search services, topics or keywords..."
            className="h-12 rounded-full border-0 bg-white pl-11 text-navy shadow-lg"
          />
        </div>
      </PageHero>

      <section className="bg-cream py-14">
        <div className="container-x">
          <div className="mb-8 flex flex-wrap gap-2">
            {SERVICE_CATEGORIES.map((c) => (
              <button
                key={c.value}
                data-testid={`service-filter-${c.value}`}
                onClick={() => setCat(c.value)}
                className={`rounded-full border px-4 py-1.5 text-sm font-medium transition-colors ${
                  cat === c.value
                    ? "border-navy bg-navy text-white"
                    : "border-slate-300 bg-white text-slate-600 hover:border-navy"
                }`}
              >
                {c.label}
              </button>
            ))}
          </div>

          {filtered.length === 0 ? (
            <p className="py-12 text-center text-slate-500" data-testid="services-empty">
              No services match your search. Try a different keyword or category.
            </p>
          ) : (
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {filtered.map((s) => <ServiceCard key={s.id} service={s} />)}
            </div>
          )}

          <div className="mt-10 flex flex-col items-start justify-between gap-5 rounded-xl border border-gold/30 bg-white p-7 md:flex-row md:items-center">
            <div className="flex items-start gap-4">
              <HelpCircle className="mt-1 shrink-0 text-gold" size={26} />
              <div>
                <h3 className="font-serif text-xl font-semibold text-navy">I'm not sure which service I need</h3>
                <p className="mt-1 text-sm text-slate-600">Tell us about your situation and we will help identify the right route.</p>
              </div>
            </div>
            <Link to="/book-consultation" data-testid="services-not-sure-cta" className="btn-gold shrink-0">
              Book a Consultation <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      <CTABand />
    </>
  );
}
